<template>
  <v-sheet color="transparent" elevation="0" class="pricing-table">
    <v-row no-gutters class="align-center">
      <!-- left side header -->
      <v-col cols="12" md="3" class="px-4">
        <v-card height="100" elevation="0"
          class="d-flex flex-column align-center justify-center text-center rounded-lg px-4 py-3 mx-auto overflow-visible position-relative border-thin border-outlined-color">
          <!--   Price & Billing -->
          <span class="font-weight-bold text-uppercase text-grey-darken-1 text-caption mb-2">
            Billing Cycle
          </span>
          <v-btn-toggle v-model="planType.isMonthly" density="compact" color="brand-color" group variant="outlined"
            class="mt-1" rounded="lg">
            <v-btn :value="true" size="small" class="rounded-s-lg" rounded="0"> Monthly </v-btn>
            <v-btn :value="false" size="small" class="rounded-e-lg" rounded="0"> Yearly</v-btn>
          </v-btn-toggle>
        </v-card>
      </v-col>
      <!-- right side header -->
      <v-col v-for="plan in displayedPlans" :key="plan.key" cols="12" md="3" class="px-4">
        <v-card height="100" :class="getPlanCardClasses(plan)" elevation="0" :role="isMobile ? 'button' : null"
          :aria-pressed="isMobile ? isSelectedOnMobile(plan) : null" :tabindex="isMobile ? 0 : null"
          @click="handlePlanSelect(plan.key)" @keydown.enter.prevent="handlePlanSelect(plan.key)"
          @keydown.space.prevent="handlePlanSelect(plan.key)">
          <!-- Popular Badge -->
          <div v-if="plan.isPopular" class="d-flex justify-center mt-n6 mb-2">
            <v-chip color="brand-color" density="compact" label rounded="lg" variant="flat"
              class="text-white text-caption font-weight-medium">
              🔥 Most Popular
            </v-chip>
          </div>
          <!-- Price & Billing -->
          <span class="font-weight-bold text-uppercase text-grey-darken-1 text-caption">
            {{ plan.name }}
          </span>
          <span class="font-weight-bold text-grey-darken-4 text-h5">
            ${{ plan.pricing[planType.isMonthly ? 'monthly' : 'yearly'] }}
            <span class="text-body-2 text-grey-darken-3">
              / month
            </span>
          </span>
          <span class="text-grey-darken-1 text-body-2 ma-0">
            Billed {{ planType.isMonthly ? 'monthly' : 'yearly' }}
          </span>
        </v-card>
      </v-col>
    </v-row>
  </v-sheet>
  <!-- table items with tooltips -->
  <v-expansion-panels v-model="expandedPanels" class="pricing-table__panels px-4 py-4" rounded="lg" elevation="0"
    multiple>
    <v-expansion-panel v-for="(category, index) in featureCategories" :key="category.key" elevation="0" rounded="lg"
      class="pricing-table__panel border-thin border-outlined-color"
      :class="{ 'mb-4': index !== featureCategories.length - 1, 'pricing-table__panel--stacked': index !== 0 }">
      <v-expansion-panel-title class="bg-grey-lighten-5 pricing-table__panel-title">
        <v-icon :icon="category.icon" class="mr-3" color="brand-color" />
        <span class="font-weight-medium text-grey-darken-3">{{ category.label }}</span>
      </v-expansion-panel-title>
      <v-expansion-panel-text class="pa-4">
        <v-row v-for="(feature, featureIndex) in getCategoryFeatures(category.key)" :key="feature.key" no-gutters
          class="py-3 align-center"
          :class="{ 'border-b': featureIndex !== getCategoryFeatures(category.key).length - 1 }">
          <v-col cols="12" md="3" class="px-2">
            <div class="d-flex align-center">
              <span class="text-body-2 text-grey-darken-3 font-weight-medium">{{ feature.label }}</span>
              <v-tooltip v-if="feature.tooltip" location="top" max-width="300">
                <template v-slot:activator="{ props }">
                  <v-icon v-bind="props" icon="mdi-information" size="x-small"
                    class="ml-2 text-grey-darken-1 cursor-pointer" />
                </template>
                <span class="text-white font-weight-medium text-tooltip">
                  {{ getInfoTooltip(feature) }}
                </span>
              </v-tooltip>
            </div>
          </v-col>
          <v-col v-for="plan in visiblePlans" :key="plan.key" cols="12" md="3" class="px-2">
            <div :class="[
              'd-flex w-100',
              isMobile ? 'justify-end align-center text-right' : 'flex-column align-center justify-center text-center'
            ]">
              <span v-if="getFeatureValue(plan, feature.key) === true" class="text-success">
                ✔
              </span>
              <span v-else-if="getFeatureValue(plan, feature.key) === false" class="text-error">
                ✘
              </span>
              <template v-else-if="typeof getFeatureValue(plan, feature.key) === 'string'">
                <v-tooltip v-if="feature.key === 'support'" location="top" max-width="300">
                  <template v-slot:activator="{ props }">
                    <span v-bind="props" :class="[
                      'text-body-2 text-grey-darken-3 font-weight-medium text-decoration-underline cursor-pointer',
                      isMobile ? 'text-right' : ''
                    ]">
                      {{ getFeatureValue(plan, feature.key) }}
                    </span>
                  </template>
                  <span class="text-white font-weight-medium text-tooltip">
                    {{ getFeatureTooltip(plan, feature.key, feature.tooltip) }}
                  </span>
                </v-tooltip>
                <span v-else
                  :class="['text-body-2 text-grey-darken-3 font-weight-medium', isMobile ? 'text-right' : 'text-center']">
                  {{ getFeatureValue(plan, feature.key) }}
                </span>
              </template>
              <span v-else :class="['text-grey', isMobile ? 'text-right' : 'text-center']">
                —
              </span>
            </div>
          </v-col>
        </v-row>
      </v-expansion-panel-text>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script>
import { FEATURE_CATEGORIES, getFeaturesByCategory } from '@/shared/constants/pricing/data'

export default {
  name: 'PTable',
  props: {
    pricingPlansWithDetails: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      planType: {
        isMonthly: true
      },
      expandedPanels: [0],
      selectedPlanKey: null
    }
  },
  computed: {
    displayedPlans() {
      return this.pricingPlansWithDetails || []
    },
    featureCategories() {
      return Object.entries(FEATURE_CATEGORIES).map(([key, category]) => ({
        key,
        ...category
      }))
    },
    isMobile() {
      return this.$vuetify?.display?.smAndDown ?? false
    },
    visiblePlans() {
      if (this.isMobile) {
        if (this.selectedPlanKey) {
          const match = this.displayedPlans.find(plan => plan.key === this.selectedPlanKey)
          if (match) return [match]
        }
        return this.displayedPlans.slice(0, 1)
      }
      return this.displayedPlans
    }
  },
  watch: {
    displayedPlans: {
      immediate: true,
      handler(plans) {
        if (!plans.length) {
          this.selectedPlanKey = null
          return
        }
        if (this.isMobile) {
          const hasSelection = plans.some(plan => plan.key === this.selectedPlanKey)
          if (!hasSelection) {
            this.selectedPlanKey = plans[0].key
          }
        }
      }
    },
    isMobile: {
      immediate: true,
      handler(value) {
        if (!value) {
          this.selectedPlanKey = null
        } else if (!this.selectedPlanKey && this.displayedPlans.length) {
          this.selectedPlanKey = this.displayedPlans[0].key
        }
      }
    }
  },
  methods: {
    getCategoryFeatures(categoryKey) {
      // Get features from the first plan (all plans should have the same features)
      if (this.displayedPlans.length === 0) return []
      const firstPlan = this.displayedPlans[0]
      return getFeaturesByCategory(firstPlan.key, categoryKey)
    },
    getFeatureValue(plan, featureKey) {
      return plan.comparison[featureKey]?.limit
    },
    getFeatureTooltip(plan, featureKey, fallback) {
      return plan.comparison[featureKey]?.tooltip || fallback
    },
    getInfoTooltip(feature) {
      if (feature.key === 'support') {
        return 'Hover the underlined support tier in each column for plan-specific support details.'
      }
      return feature.tooltip
    },
    handlePlanSelect(planKey) {
      if (!this.isMobile) return
      this.selectedPlanKey = planKey
    },
    isSelectedOnMobile(plan) {
      return this.isMobile && this.selectedPlanKey === plan.key
    },
    getPlanCardClasses(plan) {
      const classes = [
        'pricing-table__plan',
        'd-flex',
        'flex-column',
        'align-center',
        'justify-center',
        'text-center',
        'rounded-lg',
        'px-4',
        'py-3',
        'mx-auto',
        'overflow-visible',
        'position-relative',
        'my-3',
        'border-thin'
      ]

      if (this.isSelectedOnMobile(plan)) {
        classes.push('bg-blue-grey-lighten-5', 'border-outlined-color')
      } else if (plan.isPopular) {
        classes.push('pricing-table__plan--popular', 'border-brand-color')
      } else {
        classes.push('border-outlined-color')
      }

      return classes
    }
  }
}
</script>
